import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar, message, Button, Drawer } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { TabBar } from 'antd-mobile';
import {
  UserOutlined,
  MenuOutlined,
  CloudOutlined,
  SettingOutlined,
  DashboardOutlined,
  GlobalOutlined,
  BankOutlined,
  OrderedListOutlined,
  TeamOutlined,
  FileTextOutlined,
  ApartmentOutlined,
  HistoryOutlined,
  CloseOutlined,
  LogoutOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import API_BASE_URL from '../config/api';
import axios from 'axios';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SystemIcon from '../components/SystemIcon';
import './MainLayout.css';

const { Header, Content, Sider } = Layout;

// เมนูหลักสำหรับ Bottom Tab (มือถือ) — Dashboard / แผนที่ / รายชื่อวัด
const BOTTOM_TABS = [
  { key: '/dashboard', icon: <DashboardOutlined />, title: 'ภาพรวม' },
  { key: '/maps', icon: <GlobalOutlined />, title: 'แผนที่' },
  { key: '/temples', icon: <BankOutlined />, title: 'รายชื่อวัด' },
];

const MainLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname;

  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const { settings } = useSiteSettings();

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = {
        ...res.data,
        address: {
          province: res.data.province,
          district: res.data.district,
          subdistrict: res.data.subdistrict
        }
      };
      setProfile(data);
    } catch (err) {
      message.error('ไม่สามารถโหลดข้อมูลโปรไฟล์');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch(logout());
    message.success('ออกจากระบบเรียบร้อยแล้ว');
    navigate('/');
  };

  // เช็คสิทธิ์ RBAC (ระดับ ภาค = มีทุกสิทธิ์ — backward compatible)
  const can = (code) => {
    if (profile?.level === 'ภาค') return true;
    return (profile?.permissions || []).includes(code);
  };

  const handleProfile = () => {
    navigate('/me');
    setMenuOpen(false);
  };

  // เมนูทั้งหมด — ใช้กับ Sider (desktop) และ Hamburger Menu (มือถือ)
  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'ภาพรวม' },
    { key: '/maps', icon: <GlobalOutlined />, label: 'แผนที่วัด' },
    { key: '/temples', icon: <BankOutlined />, label: 'ข้อมูลวัด' },
    { key: '/temples/order', icon: <OrderedListOutlined />, label: 'จัดเรียงลำดับวัด' },
    { key: '/monks', icon: <TeamOutlined />, label: 'พระภิกษุ/สามเณร' },
    { key: '/news-managments', icon: <FileTextOutlined />, label: 'จัดการข่าว' },
    ...(can('user.manage')
      ? [{ key: '/users', icon: <UserOutlined />, label: 'จัดการผู้ใช้' }]
      : []),
    ...(can('user.assign_permissions')
      ? [{ key: '/roles', icon: <ApartmentOutlined />, label: 'จัดการบทบาท/สิทธิ์' }]
      : []),
    ...(can('audit.view')
      ? [{ key: '/audit-logs', icon: <HistoryOutlined />, label: 'บันทึกการใช้งาน' }]
      : []),
    ...(can('settings.site')
      ? [{ key: '/settings/site', icon: <SettingOutlined />, label: 'ตั้งค่าเว็บไซต์' }]
      : []),
    ...(can('settings.storage')
      ? [{ key: '/settings/googledrive', icon: <CloudOutlined />, label: 'ตั้งค่า Google Drive' }]
      : []),
  ];

  // ตรง key ให้ highlight ถูกแม้อยู่หน้า detail (/temples/:id, /news-detail/:id ...)
  const getSelectedKey = (path) => {
    const detailMap = {
      '/news-detail': '/news-managments',
      '/print-preview': '/temples',
      '/me': '/users',
      '/settings/nextcloud': '/settings/googledrive',
    };
    if (detailMap[path]) return detailMap[path];
    if (menuItems.some((m) => m.key === path)) return path;
    const parent = menuItems.find((m) => path.startsWith(`${m.key}/`));
    return parent ? parent.key : path;
  };

  // เมนูหลัก 3 ปุ่มล่าง — ระบุ active key ตาม path
  const getBottomKey = (path) => {
    const tab = BOTTOM_TABS.find(
      (t) => path === t.key || path.startsWith(`${t.key}/`)
    );
    return tab ? tab.key : '';
  };

  const userMenuItems = [
    { key: 'home', icon: <HomeOutlined />, label: 'หน้าหลัก' },
    { key: 'me', icon: <UserOutlined />, label: 'โปรไฟล์' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'ออกจากระบบ' },
  ];

  const onUserMenuClick = ({ key }) => {
    if (key === 'home') navigate('/');
    else if (key === 'me') handleProfile();
    else if (key === 'logout') handleLogout();
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        breakpoint="md"
        collapsedWidth="0"
        onBreakpoint={(broken) => {
          if (!broken) setMenuOpen(false);
        }}
        style={{
          background: '#ad7c00',
          position: 'fixed',
          height: '100vh',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
        }}
        width={200}
      >
        <div className="admin-sider-brand">
          <SystemIcon icon={settings.system_icon} size={26} />
          <span>สารสนเทศคณะสงฆ์</span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey(currentPath)]}
          onClick={({ key }) => navigate(key)}
          items={menuItems}
          style={{ background: '#ad7c00' }}
        />
      </Sider>

      {/* Hamburger Menu แบบเต็มจอ (มือถือ) */}
      <Drawer
        placement="left"
        width="100%"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        closable={false}
        zIndex={1100}
        styles={{
          body: {
            padding: 0,
            background: 'linear-gradient(180deg, #43290f 0%, #5a3a1a 100%)',
          },
        }}
        className="admin-menu-drawer"
      >
        <div className="admin-menu-drawer-body">
          <div className="admin-menu-drawer-header">
            <div className="admin-menu-brand">
              <SystemIcon icon={settings.system_icon} size={28} />
              <div>
                <div className="admin-menu-brand-title">ระบบสารสนเทศคณะสงฆ์</div>
                <div className="admin-menu-brand-sub">ภาค ๔-๕ (ธรรมยุต)</div>
              </div>
            </div>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setMenuOpen(false)}
              className="admin-menu-close"
              aria-label="ปิดเมนู"
            />
          </div>

          <div className="admin-drawer-user">
            <Avatar size={44} icon={<UserOutlined />} />
            <div className="admin-drawer-user-info">
              <div className="admin-drawer-user-name">{user?.name || 'ผู้ใช้งาน'}</div>
              <div className="admin-drawer-user-level">
                {user?.level ? `ระดับ ${user.level}` : user?.username || ''}
              </div>
            </div>
          </div>

          <Menu
            mode="inline"
            selectedKeys={[getSelectedKey(currentPath)]}
            onClick={({ key }) => {
              navigate(key);
              setMenuOpen(false);
            }}
            items={menuItems}
            className="admin-drawer-menu"
            style={{ background: 'transparent', borderInlineEnd: 'none' }}
          />

          <div className="admin-menu-drawer-footer">
            <Button
              block
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="admin-menu-logout"
            >
              ออกจากระบบ
            </Button>
          </div>
        </div>
      </Drawer>

      <Layout>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMenuOpen(true)}
              style={{ marginRight: 16 }}
              className="mobile-menu-toggle"
              aria-label="เปิดเมนู"
            />
            <div
              className="main-header-title"
              style={{ color: '#000', fontWeight: 'bold', fontSize: 20, marginLeft: collapsed ? 0 : 260 }}
            >
              <span className="main-header-title-full">ระบบสารสนเทศคณะสงฆ์ ภาค ๔-๕ (ธรรมยุต)</span>
              <span className="main-header-title-short">สารสนเทศคณะสงฆ์</span>
            </div>
          </div>

          <Dropdown
            menu={{ items: userMenuItems, onClick: onUserMenuClick }}
            placement="bottomRight"
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
              <span>{user?.name || 'ผู้ใช้งาน'}</span>
            </div>
          </Dropdown>
        </Header>

        <Content className="admin-content" style={{ margin: 16, marginLeft: collapsed ? 0 : 260 }}>
          <Outlet />
        </Content>
      </Layout>

      {/* Bottom Tab Bar (มือถือ) — เมนูหลัก 3 ปุ่ม */}
      <div className="admin-bottom-tab">
        <TabBar activeKey={getBottomKey(currentPath)} onChange={(key) => navigate(key)} safeArea>
          {BOTTOM_TABS.map((tab) => (
            <TabBar.Item key={tab.key} icon={tab.icon} title={tab.title} />
          ))}
        </TabBar>
      </div>
    </Layout>
  );
};

export default MainLayout;
